import React, { useState, useEffect } from 'react';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import RiskHelp from './RiskHelp';
import UserManager from './UserManager';
import WalletTransfer from './WalletTransfer';
import SmsSettings from './SmsSettings';
import SecuritySettings from './SecuritySettings';
import BackupSettings from './BackupSettings';
import { useTranslation } from '../i18n/LanguageContext';
import { useUserSettings } from '../hooks/useUserSettings';

// Yaygın UTC ofsetleri (dakika cinsinden) — yarım/çeyrek saatlik dilimler dahil (Hindistan +330,
// İran +210, Nepal +345 vb.). BacktestPanel.jsx/TradeChartModal.jsx/backend chart.js bu ayarı
// (utc_offset_minutes) kullanıcının kendi UTC ofseti olarak kullanır.
const UTC_OFFSET_OPTIONS = [
  -720, -660, -600, -570, -540, -480, -420, -360, -300, -240, -210, -180, -150, -120, -60, 0,
  60, 120, 180, 210, 240, 270, 300, 330, 345, 360, 390, 420, 480, 525, 540, 570, 600, 630, 660, 720, 765, 780, 840,
].map(mins => {
  const sign = mins < 0 ? '-' : '+';
  const abs  = Math.abs(mins);
  const h    = Math.floor(abs / 60);
  const m    = abs % 60;
  const label = `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
  return { value: String(mins), label };
});

// Sekme etiketleri ve alan label/hint metinleri `t()`'a bağlı olduğu için (bileşen dışında hook
// çağrılamaz) fonksiyon olarak tanımlanır — bkz. BacktestPanel.jsx'teki aynı desen.
const getAllTabs = (t) => [
  { id: 'general',  label: t('settings.tabGeneral') },
  { id: 'okx',      label: t('settings.tabApi') },
  { id: 'users',    label: t('settings.tabUsers'), systemOnly: true },
  { id: 'sms',      label: t('settings.tabSms'),   systemOnly: true },
  { id: 'security', label: t('settings.tabSecurity'), systemOnly: true },
  { id: 'backups',  label: t('settings.tabBackups'), systemOnly: true },
  { id: 'signal',   label: t('settings.tabSignal') },
  { id: 'risk',     label: t('settings.tabRisk') },
  { id: 'wallet',   label: t('settings.tabWallet') },
];

const getFieldMeta = (t) => ({
  // Genel — kullanıcının kendi UTC ofseti. Grafik mum zamanları, backtest tarih girişi/çıkışı ve
  // işlem görselleştirme anlatımı buna göre gösterilir (bkz. useUserSettings hook'u).
  utc_offset_minutes: { label: t('settings.utcOffset'), secret: false, tab: 'general', type: 'select', hint: t('settings.utcOffsetHint'), options: UTC_OFFSET_OPTIONS },
  // API — Base URL en üstte: hangi platform seçiliyse aşağıdaki API Key/Secret/Passphrase o platforma
  // ait ayrı saklanan kimlik bilgisi setini gösterir (bkz. `platform` alanı + currentBaseUrl filtresi).
  okx_base_url:   { label: t('settings.baseUrl'),   secret: false, tab: 'okx', type: 'select', options: [
    { value: 'https://www.okx.com', label: 'OKX Global' },
    { value: 'https://tr.okx.com',  label: 'OKX TR' },
  ] },
  okx_global_api_key:    { label: t('settings.apiKey'),     secret: true, tab: 'okx', platform: 'https://www.okx.com' },
  okx_global_secret_key: { label: t('settings.secretKey'),  secret: true, tab: 'okx', platform: 'https://www.okx.com' },
  okx_global_passphrase: { label: t('settings.passphrase'), secret: true, tab: 'okx', platform: 'https://www.okx.com' },
  okx_tr_api_key:        { label: t('settings.apiKey'),     secret: true, tab: 'okx', platform: 'https://tr.okx.com' },
  okx_tr_secret_key:     { label: t('settings.secretKey'),  secret: true, tab: 'okx', platform: 'https://tr.okx.com' },
  okx_tr_passphrase:     { label: t('settings.passphrase'), secret: true, tab: 'okx', platform: 'https://tr.okx.com' },
  // Risk (evrensel — strategies belirtilmeyen alanlar her stratejide gösterilir)
  sl_pct:          { label: t('settings.fieldSlPct.label'),          secret: false, tab: 'risk', type: 'number', unit: '%',     hint: t('settings.fieldSlPct.hint') },
  // ATR Periyodu/SL Çarpanı/SL Cooldown artık rsi_rsima/rsima50'de YOK (kullanıcı kararı — bu 2
  // stratejide ATR yedek-sigorta fallback'ı ve cooldown kavramı kaldırıldı, bkz. tp_pct/sl_pct
  // aşağıda). Diğer stratejilerde (range_hma_adx/trend_pullback_r) davranış AYNEN korunur.
  atr_period:      { label: t('settings.fieldAtrPeriod.label'),        secret: false, tab: 'risk', type: 'number', unit: 'bar',   hint: t('settings.fieldAtrPeriod.hint'), strategies: ['range_hma_adx', 'trend_pullback_r'] },
  atr_sl_mult:     { label: t('settings.fieldAtrSlMult.label'),    secret: false, tab: 'risk', type: 'number', unit: '× ATR', hint: t('settings.fieldAtrSlMult.hint'), strategies: ['range_hma_adx', 'trend_pullback_r'] },
  sl_cooldown_min: { label: t('settings.fieldSlCooldown.label'),         secret: false, tab: 'risk', type: 'number', unit: 'dk',    hint: t('settings.fieldSlCooldown.hint'), strategies: ['range_hma_adx', 'trend_pullback_r'] },
  // TP Yüzdesi — YALNIZCA rsi_rsima/rsima50 (yeni özellik, kullanıcı kararı). 0/boş = TP kapalı.
  tp_pct:          { label: t('settings.fieldTpPct.label'),          secret: false, tab: 'risk', type: 'number', unit: '%',     hint: t('settings.fieldTpPct.hint'), strategies: ['rsi_rsima', 'rsima50'] },
  // HMA/ADX (yalnızca range_hma_adx)
  hma_period:         { label: t('settings.fieldHmaPeriod.label'),            secret: false, tab: 'risk', type: 'number', unit: 'bar', hint: t('settings.fieldHmaPeriod.hint'), strategies: ['range_hma_adx'] },
  adx_period:         { label: t('settings.fieldAdxPeriod.label'),            secret: false, tab: 'risk', type: 'number', unit: 'bar', hint: t('settings.fieldAdxPeriod.hint'), strategies: ['range_hma_adx', 'trend_pullback_r'] },
  adx_threshold:      { label: t('settings.fieldAdxThreshold.label'),                secret: false, tab: 'risk', type: 'number', unit: '',    hint: t('settings.fieldAdxThreshold.hint'), strategies: ['range_hma_adx', 'trend_pullback_r'] },
  range_box_atr_mult: { label: t('settings.fieldRangeBoxMult.label'),             secret: false, tab: 'risk', type: 'number', unit: '× ATR', hint: t('settings.fieldRangeBoxMult.hint'), strategies: ['range_hma_adx'] },
  rsi_deadzone:       { label: t('settings.fieldRsiDeadzone.label'),            secret: false, tab: 'risk', type: 'number', unit: 'puan', hint: t('settings.fieldRsiDeadzone.hint'), strategies: ['rsi_rsima', 'rsima50'] },
  pullback_atr_mult:  { label: t('settings.fieldPullbackMult.label'),        secret: false, tab: 'risk', type: 'number', unit: '× ATR', hint: t('settings.fieldPullbackMult.hint'), strategies: ['trend_pullback_r'] },
  rsi_trigger:        { label: t('settings.fieldRsiTrigger.label'),      secret: false, tab: 'risk', type: 'number', unit: '',    hint: t('settings.fieldRsiTrigger.hint'), strategies: ['trend_pullback_r'] },
});

const FIELD_DEFAULTS = {
  utc_offset_minutes: '180',
  okx_base_url:       'https://www.okx.com',
  sl_pct:             '0',
  tp_pct:             '0',
  atr_period:         '14',
  atr_sl_mult:        '1.2',
  sl_cooldown_min:    '5',
  rsi_deadzone:       '0',
  pullback_atr_mult:  '1.0',
  rsi_trigger:        '45',
  // Strateji
  analysis_strategy:  '',
  hma_period:         '21',
  adx_period:         '14',
  adx_threshold:      '0',
  range_box_atr_mult: '1.0',
  show_hma_indicator: 'false',
  show_adx_indicator: 'false',
};

const STRATEGY_OPTIONS = [
  { value: 'range_hma_adx',    label: 'Range Bar + HMA + ADX' },
  { value: 'rsi_rsima',        label: 'RSI + SMA' },
  { value: 'rsima50',          label: 'SMA - 50 Seviyesi' },
  { value: 'trend_pullback_r', label: 'Trend-Pullback + R' },
];

function SignalInfo({ form, onFieldChange }) {
  const { t, lang } = useTranslation();
  const isEn = lang === 'en';
  const strategy = form.analysis_strategy;
  // Boş VEYA artık kaldırılmış/tanınmayan bir değer (ör. eski 'hma_adx' kaydı) — sessizce bir
  // varsayılana düşülmez, kullanıcı açıkça uyarılır (bkz. kullanıcı kararı).
  const strategyInvalid = !strategy || !STRATEGY_OPTIONS.some(opt => opt.value === strategy);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Strateji Seçimi */}
      <div className="settings-field">
        <label className="settings-label">{t('settings.strategySelectTitle')}</label>
        <span className="settings-hint">{t('settings.strategySelectHint')}</span>
        {strategyInvalid && (
          <div style={{ background: 'rgba(255,59,59,0.1)', border: '1px solid rgba(255,59,59,0.4)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--red, #ff3b3b)', marginTop: 6, marginBottom: 4 }}>
            {strategy
              ? (isEn ? `Your selected strategy ('${strategy}') is no longer supported — please pick one below.` : `Seçili stratejiniz ('${strategy}') artık desteklenmiyor — lütfen aşağıdan yeni bir strateji seçin.`)
              : (isEn ? 'No strategy selected — bots and backtests cannot run until you pick one below.' : 'Hiçbir strateji seçilmemiş — siz aşağıdan bir strateji seçmeden bot/backtest çalıştırılamaz.')}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {STRATEGY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onFieldChange('analysis_strategy', opt.value)}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: strategy === opt.value ? 'var(--accent)' : 'transparent',
                color:      strategy === opt.value ? '#fff' : 'var(--text)',
                border:     strategy === opt.value ? '1px solid var(--accent)' : '1px solid var(--border)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Strateji B: Range Bar + HMA + ADX ── */}
      {strategy === 'range_hma_adx' && (
        <div style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8, fontSize: 13 }}>
            Range Bar + HMA + ADX — {isEn ? 'Rule Set' : 'Kural Seti'}
          </div>
          {(
            /* Genel bakış / eğitim içeriği — uzun olduğu için kendi dikey scroll'u var. */
            <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10, paddingRight: 4 }}>
              {isEn ? (
                <>
                  <p className="settings-note" style={{ marginBottom: 10 }}>
                    This strategy runs entirely on price-sensitive Range Bars instead of time-based
                    (minute/hourly) candles. It produces instant, state-based decisions without waiting
                    for a candle close.
                  </p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Chart and Infrastructure Settings (Range Bar)</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    The bars the strategy is calculated on are independent of the time frame, and form
                    once price moves by a certain box size.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 6, fontWeight: 700, color: 'var(--text)' }}>
                    Box Size = ATR × Box Multiplier
                  </p>
                  <p className="settings-note" style={{ marginBottom: 10 }}>
                    Note: this is fixed throughout the analysis window. Advantage: if price whipsawed
                    inside a large, sharp time-based candle, multiple range bars can form and the system
                    can catch more than one direction change within a single candle.
                  </p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Indicator and Filter Conditions</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Hull Moving Average (HMA) direction and an optional ADX filter are used for trade
                    entries and exits.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    HMA Direction: the instant slope of the HMA curve is looked at. An upward slope
                    represents the UP (Bullish) direction, a downward slope the DOWN (Bearish) direction.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 2 }}>ADX Filter (set from the Risk tab):</p>
                  <p className="settings-note" style={{ marginBottom: 2 }}>If ADX Threshold = 0: the ADX filter is fully disabled. The HMA direction alone is enough.</p>
                  <p className="settings-note" style={{ marginBottom: 10 }}>If ADX Threshold &gt; 0: both the HMA direction AND the ADX ≥ Threshold condition are required at the same time, on entries and exits alike.</p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>3. Market Entry and Position Management</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Whenever the system starts, or at any moment it stays in cash (flat), a trade is
                    opened instantly according to the current indicator state — without waiting for any
                    breakout/signal (flip). There is no Take Profit (TP) in this strategy. Only a backup
                    insurance Stop-Loss (SL), set at entry and unchanged afterward, is used.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    The trade cycle differs by market type as follows:
                  </p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '8px 0 4px' }}>A) Futures — Always Staying in the Market (Always-in)</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    On futures, the position is held continuously regardless of direction — it never waits in cash.
                  </p>
                  <div className="sh-table-wrap" style={{ marginBottom: 10 }}>
                    <table className="sh-table">
                      <thead><tr><th>Current Status</th><th>Direction Change Condition</th><th>Action</th></tr></thead>
                      <tbody>
                        <tr><td>LONG Position</td><td>HMA direction turns down (+ ADX threshold if set)</td><td>The LONG position closes instantly, and SHORT opens at the same time without waiting.</td></tr>
                        <tr><td>SHORT Position</td><td>HMA direction turns up (+ ADX threshold if set)</td><td>The SHORT position closes instantly, and LONG opens at the same time without waiting.</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '8px 0 4px' }}>B) Spot — One-Directional Trading</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Since there is no shorting on spot, the system waits in cash (flat) during downturns.
                  </p>
                  <div className="sh-table-wrap" style={{ marginBottom: 0 }}>
                    <table className="sh-table">
                      <thead><tr><th>Current Status</th><th>Direction Change Condition</th><th>Action</th></tr></thead>
                      <tbody>
                        <tr><td>Flat (Cash)</td><td>HMA direction turns up (+ ADX threshold if set)</td><td>A LONG position opens instantly.</td></tr>
                        <tr><td>LONG Position</td><td>HMA direction turns down (+ ADX threshold if set)</td><td>The LONG position closes instantly and the system moves to the Flat (Cash) state. No new trade opens until the next upward signal.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <p className="settings-note" style={{ marginBottom: 10 }}>
                    Bu strateji, zaman-bazlı (dakikalık/saatlik) mumlar yerine tamamen fiyata duyarlı
                    Range Bar (Mesafe Çubukları) üzerinde çalışır. Mum kapanışı beklemeden, anlık ve
                    durum-tabanlı kararlar üretir.
                  </p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Grafik ve Altyapı Ayarları (Range Bar)</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Stratejinin üzerinde hesaplandığı barlar, zaman diliminden bağımsızdır ve fiyatın
                    belirli bir kutu (box) büyüklüğü kadar hareket etmesiyle oluşur.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 6, fontWeight: 700, color: 'var(--text)' }}>
                    Kutu Büyüklüğü = ATR × Box Çarpanı
                  </p>
                  <p className="settings-note" style={{ marginBottom: 10 }}>
                    Bu, analiz penceresi boyunca sabittir. Avantajı: Büyük ve sert bir zaman-bazlı mumun
                    içinde, fiyat git-gel yaptıysa birden fazla range bar oluşabilir ve sistem tek bir
                    mum içinde birden fazla yön değişimi yakalayabilir.
                  </p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. İndikatör ve Filtre Koşulları</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    İşleme giriş ve çıkışlarda Hull Moving Average (HMA) yönü ve opsiyonel ADX filtresi
                    kullanılır.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    HMA Yönü: HMA eğrisinin anlık eğimine bakılır. Yukarı eğim YUKARI (Bullish), aşağı
                    eğim AŞAĞI (Bearish) yönü temsil eder.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 2 }}>ADX Filtresi (Risk Sekmesinden Ayarlanır):</p>
                  <p className="settings-note" style={{ marginBottom: 2 }}>ADX Eşiği = 0 ise: ADX filtresi tamamen devre dışıdır. Sadece HMA yönü yeterlidir.</p>
                  <p className="settings-note" style={{ marginBottom: 10 }}>ADX Eşiği &gt; 0 ise: İşleme giriş ve çıkışlarda hem HMA yönü hem de ADX ≥ Eşik şartı aynı anda aranır.</p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>3. Piyasaya Giriş ve Pozisyon Yönetimi</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Sistem başlatıldığında veya nakitte (flat) kalındığı her an, herhangi bir kırılım/sinyal
                    (flip) beklenmeksizin o anki güncel indikatör durumuna göre anında işlem açılır.
                    Stratejide Kâr Al (TP) yoktur. Sadece giriş anında belirlenen ve değişmeyen bir Yedek
                    Sigorta Stop-Loss (SL) kullanılır.
                  </p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Piyasa türüne göre işlem döngüsü şu şekildedir:
                  </p>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '8px 0 4px' }}>A) Vadeli İşlemler (Futures) — Sürekli Piyasada Kalma (Always-in)</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Vadeli piyasada yön ne olursa olsun sürekli pozisyonda kalınır, nakitte beklenmez.
                  </p>
                  <div className="sh-table-wrap" style={{ marginBottom: 10 }}>
                    <table className="sh-table">
                      <thead><tr><th>Mevcut Durum</th><th>Yön Değişim Şartı</th><th>Aksiyon</th></tr></thead>
                      <tbody>
                        <tr><td>LONG Pozisyon</td><td>HMA yönünün aşağı dönmesi (+ varsa ADX eşiği)</td><td>LONG pozisyon anında kapatılır, bekleme olmaksızın aynı anda SHORT açılır.</td></tr>
                        <tr><td>SHORT Pozisyon</td><td>HMA yönünün yukarı dönmesi (+ varsa ADX eşiği)</td><td>SHORT pozisyon anında kapatılır, bekleme olmaksızın aynı anda LONG açılır.</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '8px 0 4px' }}>B) Spot İşlemler — Tek Yönlü İşlem</p>
                  <p className="settings-note" style={{ marginBottom: 6 }}>
                    Spot piyasada short (açığa satış) olmadığı için sistem düşüşlerde nakitte (flat) bekler.
                  </p>
                  <div className="sh-table-wrap" style={{ marginBottom: 0 }}>
                    <table className="sh-table">
                      <thead><tr><th>Mevcut Durum</th><th>Yön Değişim Şartı</th><th>Aksiyon</th></tr></thead>
                      <tbody>
                        <tr><td>Flat (Nakit)</td><td>HMA yönünün yukarı dönmesi (+ varsa ADX eşiği)</td><td>Anında LONG pozisyon açılır.</td></tr>
                        <tr><td>LONG Pozisyon</td><td>HMA yönünün aşağı dönmesi (+ varsa ADX eşiği)</td><td>LONG pozisyon anında kapatılır ve Flat (Nakit) durumuna geçilir. Bir sonraki yukarı sinyale kadar yeni işlem açılmaz.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.show_hma_indicator === 'true'}
                onChange={e => onFieldChange('show_hma_indicator', e.target.checked ? 'true' : 'false')}
              />
              {t('settings.showHmaIndicator')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.show_adx_indicator === 'true'}
                onChange={e => onFieldChange('show_adx_indicator', e.target.checked ? 'true' : 'false')}
              />
              {t('settings.showAdxIndicator')}
            </label>
          </div>
          <span className="settings-hint" style={{ display: 'block', marginBottom: 10 }}>
            {t('settings.indicatorHint')}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▲ LONG:</strong> {isEn ? 'HMA direction up' : 'HMA yönü yukarı'}{parseFloat(form.adx_threshold) > 0 ? ` AND ADX ≥ ${form.adx_threshold}` : ''}
            </div>
            <div style={{ background: 'rgba(255,59,59,0.08)', border: '1px solid rgba(255,59,59,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▼ SHORT:</strong> {isEn ? 'HMA direction down' : 'HMA yönü aşağı'}{parseFloat(form.adx_threshold) > 0 ? ` AND ADX ≥ ${form.adx_threshold}` : ''} ({isEn ? 'futures only' : 'yalnızca vadeli'})
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
            {isEn
              ? 'The position closes automatically when the trend reverses — reopens immediately in the opposite direction on futures, stays flat on spot awaiting the next LONG signal.'
              : 'Trend tersine dönünce pozisyon otomatik kapatılır — vadelide aynı anda ters yönde yeniden açılır, spot\'ta flat kalıp bir sonraki LONG sinyali beklenir.'}
            {parseFloat(form.adx_threshold) > 0 && <span style={{ color: 'var(--accent)' }}> {isEn ? 'The ADX threshold also applies to the close decision.' : 'ADX eşiği kapanış kararında da geçerlidir.'}</span>}
          </div>
        </div>
      )}

      {/* ── Strateji D: RSI + SMA ── */}
      {strategy === 'rsi_rsima' && (
        <div style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8, fontSize: 13 }}>RSI + SMA — {isEn ? 'Rule Set' : 'Kural Seti'}</div>

          {/* Genel bakış / eğitim içeriği — uzun olduğu için kendi dikey scroll'u var. */}
          <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10, paddingRight: 4 }}>
            {isEn ? (
              <>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  This strategy aims to never stay in cash (flat) in the market; the moment a position
                  closes, a new position in the opposite direction is opened instantly. All entries and
                  exits happen with real-time data, without waiting for a candle close.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Initial Entry Conditions (First Trade Entry)</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  While there is no position in the system (flat state), a trade is entered unconditionally
                  and instantly based on the position of RSI relative to its own 14-period moving
                  average (SMA):
                </p>
                <p className="settings-note" style={{ marginBottom: 2 }}>LONG Entry: if RSI ≥ SMA, LONG opens instantly.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>SHORT Entry: if RSI &lt; SMA, SHORT opens instantly.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Note: at the start, the dead zone is NOT an entry gate or obstacle.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Position Management and Exit (Take Profit &amp; Stop-Loss)</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Take Profit (TP): there is no fixed Take Profit target in this strategy. The position is carried until the trend ends.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>Stop-Loss (SL): the system only has a backup safety net (Hard Stop-Loss) for emergencies.</p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>3. Position Reversal Rules</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  The "RSI Dead Zone" parameter acts as a direction-change buffer around SMA. For the
                  position to close and instantly flip to the opposite direction, RSI must fully cross
                  this buffer zone.
                </p>
                <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
                  💡 <strong>Core Rule:</strong> The position stays open until RSI violates the buffer
                  boundary of its current side. The moment the violation happens, the position closes and
                  a new position opens in the opposite direction without any waiting.
                </div>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>📈 LONG-to-SHORT Position Switch Condition</p>
                <p className="settings-note" style={{ marginBottom: 4 }}>
                  While in a LONG position, for price to reverse and SHORT to open, the RSI value must
                  drop below SMA by the dead zone amount.
                </p>
                <p className="settings-note" style={{ marginBottom: 10, fontWeight: 700, color: 'var(--text)' }}>
                  Condition to switch to SHORT: RSI ≤ SMA − Dead Zone
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>📉 SHORT-to-LONG Position Switch Condition</p>
                <p className="settings-note" style={{ marginBottom: 4 }}>
                  While in a SHORT position, for price to reverse and LONG to open, the RSI value must
                  rise above SMA by the dead zone amount.
                </p>
                <p className="settings-note" style={{ marginBottom: 10, fontWeight: 700, color: 'var(--text)' }}>
                  Condition to switch to LONG: RSI ≥ SMA + Dead Zone
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>🔍 Application Example</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Parameter: let Dead Zone = 1.</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Current Status: you are in a LONG position.</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Instant Values: RSI = 20, SMA = 19.</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>Reversal Boundary: 19 − 1 = 18</p>
                <p className="settings-note" style={{ marginBottom: 0 }}>
                  Action: the LONG position is maintained as long as the RSI value does not drop to 18
                  or below. The moment it drops below 18, LONG is closed and a SHORT position is opened
                  instantly.
                </p>
              </>
            ) : (
              <>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Bu strateji, piyasada hiçbir zaman nakitte (flat) kalmamayı hedefler; bir pozisyon
                  kapandığı an tersi yönde yeni bir pozisyon anlık olarak açılır. Tüm giriş ve çıkışlar
                  mum kapanışı beklenmeden, anlık (real-time) verilerle yapılır.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Başlangıç Giriş Koşulları (İlk İşleme Giriş)</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  Sistemde herhangi bir pozisyon yokken (flat durumunda), RSI ile kendi 14 periyotluk
                  hareketli ortalamasının (SMA) birbirine göre konumuna bakılarak koşulsuz ve anlık
                  olarak işleme girilir:
                </p>
                <p className="settings-note" style={{ marginBottom: 2 }}>LONG Giriş: RSI ≥ SMA ise anında LONG açılır.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>SHORT Giriş: RSI &lt; SMA ise anında SHORT açılır.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Not: Başlangıçta ölü bölge (dead zone) bir giriş kapısı veya engeli değildir.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Pozisyon Yönetimi ve Çıkış (Kar Al &amp; Zarar Durdur)</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Kâr Al (TP): Bu stratejide sabit bir Kâr Al (Take Profit) hedefi yoktur. Pozisyon trend bitene kadar taşınır.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>Zarar Durdur (SL): Sistemde acil durumlar için sadece bir yedek sigorta (Hard Stop-Loss) bulunur.</p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>3. Pozisyon Değişim (Tersine Dönüş) Kuralları</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  "RSI Ölü Bölge" parametresi, SMA'nın etrafında yön değiştirme tamponu olarak görev
                  yapar. Pozisyonun kapanması ve anında ters yöne dönmesi için RSI'ün bu tampon
                  bölgesini tamamen aşması gerekir.
                </p>
                <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
                  💡 <strong>Temel Kural:</strong> Pozisyon, RSI mevcut tarafın tampon sınırını ihlal
                  edene kadar açık kalır. İhlal gerçekleştiği an pozisyon kapanır ve bekleme olmaksızın
                  ters yönde yeni işlem açılır.
                </div>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>📈 LONG Pozisyondan SHORT'a Geçiş Koşulu</p>
                <p className="settings-note" style={{ marginBottom: 4 }}>
                  LONG pozisyondayken, fiyatın tersine dönüp SHORT açılması için RSI değerinin, SMA'nın
                  ölü bölge kadar altına inmesi gerekir.
                </p>
                <p className="settings-note" style={{ marginBottom: 10, fontWeight: 700, color: 'var(--text)' }}>
                  SHORT'a Dönüş Şartı: RSI ≤ SMA − Ölü Bölge
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>📉 SHORT Pozisyondan LONG'a Geçiş Koşulu</p>
                <p className="settings-note" style={{ marginBottom: 4 }}>
                  SHORT pozisyondayken, fiyatın tersine dönüp LONG açılması için RSI değerinin, SMA'nın
                  ölü bölge kadar üstüne çıkması gerekir.
                </p>
                <p className="settings-note" style={{ marginBottom: 10, fontWeight: 700, color: 'var(--text)' }}>
                  LONG'a Dönüş Şartı: RSI ≥ SMA + Ölü Bölge
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>🔍 Uygulama Örneği</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Parametre: Ölü Bölge = 1 olsun.</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Mevcut Durum: LONG pozisyondasınız.</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>Anlık Değerler: RSI = 20, SMA = 19.</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>Tersine Dönüş Sınırı: 19 − 1 = 18</p>
                <p className="settings-note" style={{ marginBottom: 0 }}>
                  Aksiyon: RSI değeri 18 veya altına düşmediği sürece LONG pozisyon korunur. 18'in
                  altına indiği an LONG kapatılır ve anında SHORT pozisyon açılır.
                </p>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▲ LONG:</strong> {isEn ? 'Entry' : 'Giriş'} RSI≥SMA → {isEn ? 'Exit' : 'Çıkış'} RSI≤SMA−{isEn ? 'DeadZone' : 'ÖlüBölge'}
            </div>
            <div style={{ background: 'rgba(255,59,59,0.08)', border: '1px solid rgba(255,59,59,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▼ SHORT:</strong> {isEn ? 'Entry' : 'Giriş'} RSI&lt;SMA → {isEn ? 'Exit' : 'Çıkış'} RSI≥SMA+{isEn ? 'DeadZone' : 'ÖlüBölge'}
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
            {isEn
              ? 'SL is set at the start using the Risk tab\'s ATR multiplier or fixed percentage. The TP field is not used in this strategy.'
              : 'SL, Risk sekmesindeki ATR çarpanı veya sabit yüzdeyle en baştan belirlenir. TP alanı bu stratejide dikkate alınmaz.'}
          </div>
        </div>
      )}

      {/* ── Strateji F: SMA - 50 Seviyesi ── */}
      {strategy === 'rsima50' && (
        <div style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8, fontSize: 13 }}>SMA - 50 {isEn ? 'Level' : 'Seviyesi'} — {isEn ? 'Rule Set' : 'Kural Seti'}</div>

          {/* Genel bakış / eğitim içeriği — uzun olduğu için kendi dikey scroll'u var. */}
          <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10, paddingRight: 4 }}>
            {isEn ? (
              <>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  This strategy compares SMA (the 14-period moving average of RSI) against a Dead Zone
                  band around 50 (width set by the Dead Zone parameter). Unlike RSI+SMA, it only
                  decides at candle close — it never reacts to the live/intra-candle price.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Entry Rule</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>LONG: at the candle close where SMA exits the Dead Zone upward (above 50 + Dead Zone).</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>SHORT: at the candle close where SMA exits the Dead Zone downward (below 50 − Dead Zone).</p>
                <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
                  💡 <strong>Dead Zone visit required:</strong> SMA must first be inside the Dead Zone
                  before an entry can trigger. If it's already outside the band when the bot starts (or
                  right after a position closes), it waits until SMA comes back into the zone and then
                  exits it again.
                </div>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Exit Rule</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  The position stays open for as long as SMA remains outside the Dead Zone band. It closes
                  the instant SMA returns inside the band (the exact mirror of the entry threshold) — this
                  is the only close condition tied to the indicator. <strong>There is no fixed Take Profit</strong> —
                  profit optimization is intentionally left for a future iteration. The fixed SL/ATR stop set
                  from the Risk tab remains as a backstop for sudden/gap moves the indicator can't react to
                  in time.
                </p>
              </>
            ) : (
              <>
                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Giriş Kuralı</p>
                <p className="settings-note" style={{ marginBottom: 2 }}>LONG: SMA'nın Ölü Bölge'den yukarı çıktığı (50 + Ölü Bölge üstüne geçtiği) mum kapanışında.</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>SHORT: SMA'nın Ölü Bölge'den aşağı çıktığı (50 − Ölü Bölge altına geçtiği) mum kapanışında.</p>
                <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
                  💡 <strong>Önce Ölü Bölge'ye uğramalı:</strong> giriş tetiklenmeden önce SMA'nın Ölü
                  Bölge'nin içinde bulunmuş olması şarttır. Bot başladığında (veya bir pozisyon kapandığında)
                  SMA zaten bandın dışındaysa, önce bandın içine dönüp sonra tekrar dışına çıkması beklenir.
                </div>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Çıkış Kuralı</p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Pozisyon, SMA Ölü Bölge bandının dışında kaldığı sürece açık kalır. SMA bandın içine
                  geri döner dönmez (giriş eşiğinin tam simetriği) kapanır — göstergeye bağlı tek kapanış
                  şartı budur. <strong>Sabit bir Kâr Al (TP) YOKTUR</strong> — kâr optimizasyonu bilinçli
                  olarak sonraki bir aşamaya bırakıldı. Risk sekmesinden belirlenen sabit SL/ATR durdurma,
                  göstergenin yetişemediği ani/gap hareketler için arka plan sigortası olarak kalır.
                </p>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▲ LONG:</strong> SMA &gt; 50 + {isEn ? 'Dead Zone' : 'Ölü Bölge'}
            </div>
            <div style={{ background: 'rgba(255,59,59,0.08)', border: '1px solid rgba(255,59,59,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▼ SHORT:</strong> SMA &lt; 50 − {isEn ? 'Dead Zone' : 'Ölü Bölge'}
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
            {isEn
              ? 'No fixed TP: the position simply rides until SMA returns to the Dead Zone. The fixed SL remains as a backstop.'
              : 'Sabit TP yok: pozisyon, SMA Ölü Bölge\'ye dönene kadar sürer. Sabit SL arka plan sigortası olarak kalır.'}
          </div>
        </div>
      )}

      {/* ── Strateji E: Trend-Pullback + R ── */}
      {strategy === 'trend_pullback_r' && (
        <div style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8, fontSize: 13 }}>Trend-Pullback + R — {isEn ? 'Rule Set' : 'Kural Seti'}</div>

          {/* Genel bakış / eğitim içeriği — uzun olduğu için kendi dikey scroll'u var, panelin
              geri kalanını (aşağıdaki teknik parametre eşleşmesi) itmesin diye. */}
          <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10, paddingRight: 4 }}>
            {isEn ? (
              <>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Trend-Pullback is a strategy especially favored by trend-following investors and day
                  traders — it aims to open a trade in the direction of a strong trend, at a "discounted"
                  price, by catching the temporary retracements (pullbacks) of that trend.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. What Is Trend-Pullback?</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  A market never goes up or down in a perfectly straight line. It moves in waves.
                  Trend-Pullback is the strategy of catching the temporary retracements (corrections) of
                  those waves in order to open a trade in the direction of a strong trend.
                </p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  <strong>Logic:</strong> After price makes a peak in a rising market, price dips slightly
                  (a pullback) as some investors take profit. This dip is a "discounted price" ticket to
                  board the train for those who missed the trend.
                </p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  <strong>How Is It Applied?</strong> While price retraces to support levels, moving
                  averages (for example the 20 or 50 EMA), or Fibonacci retracement levels (38.2% or 50%),
                  reversal signals (e.g. a hammer candle formation) are looked for, and the trade is
                  entered in the direction of the trend.
                </p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  <strong>In short:</strong> "If the trend is up, don't buy at the top; wait for the price
                  to ease off a bit (a pullback), and once it's cheaper, buy in the direction of the
                  trend."
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Exit Rule (simple by design — no trailing yet)</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  There is no fixed Take Profit, and no dynamic trailing stop — profit optimization is
                  intentionally left for a future iteration. The position simply rides for as long as the
                  MA50/MA200 trend filter stays valid.
                </p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  The position closes the instant the trend reverses (MA50 crosses MA200 against the
                  position) — a structural safety exit, same principle as HMA+ADX. The fixed SL (Risk tab)
                  remains active in parallel as a backstop for sudden/gap moves the trend filter can't
                  react to in time.
                </p>
              </>
            ) : (
              <>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Trend-Pullback (Trend-Düzeltme), özellikle trend takip eden yatırımcıların ve gün içi
                  yatırımcıların (day trader) sıkça kullandığı bir stratejidir — güçlü bir trendin geçici
                  geri çekilmelerini (pullback) yakalayarak "indirimli fiyattan" trend yönünde işleme
                  girmeyi hedefler.
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>1. Trend-Pullback Nedir?</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  Piyasa hiçbir zaman dümdüz bir çizgi halinde yukarı veya aşağı gitmez. Dalgalar halinde
                  hareket eder. Trend-Pullback, güçlü bir trendin yönünde işlem açmak için bu dalgaların
                  geçici geri çekilmelerini (düzeltmelerini) yakalama stratejisidir.
                </p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  <strong>Mantığı:</strong> Yükselen bir piyasada fiyat zirve yaptıktan sonra, bazı
                  yatırımcıların kâr almasıyla fiyat hafifçe düşer (pullback). İşte bu düşüş, trendi
                  kaçıranlar için "indirimli fiyattan" trene biniş biletidir.
                </p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  <strong>Nasıl Uygulanır?</strong> Fiyat geri çekilirken destek seviyelerine, hareketli
                  ortalamalara (örneğin 20 veya 50 EMA) ya da Fibonacci düzeltme seviyelerine (%38.2 veya
                  %50) ulaştığında geri dönüş sinyalleri (örn. çekiç mum formasyonu) aranır ve trend
                  yönünde işleme girilir.
                </p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  <strong>Özetle:</strong> "Trend yukarıysa, tepeden alma; fiyatın biraz gevşemesini
                  (pullback) bekle, ucuzlayınca trend yönünde satın al."
                </p>

                <p className="settings-note" style={{ fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' }}>2. Çıkış Kuralı (bilinçli olarak basit — henüz trailing yok)</p>
                <p className="settings-note" style={{ marginBottom: 6 }}>
                  Sabit bir Kâr Al (TP) yoktur, dinamik bir trailing stop da yoktur — kâr optimizasyonu
                  bilinçli olarak sonraki bir aşamaya bırakıldı. Pozisyon, MA50/MA200 trend filtresi
                  geçerli kaldığı sürece sürer.
                </p>
                <p className="settings-note" style={{ marginBottom: 10 }}>
                  Trend tersine döndüğü an (MA50, MA200'ü pozisyonun aleyhine kestiğinde) pozisyon anında
                  kapanır — HMA+ADX ile aynı ilkeye dayanan yapısal bir güvenlik çıkışı. Risk sekmesinden
                  belirlenen sabit SL, trend filtresinin yetişemediği ani/gap hareketler için paralelde
                  aktif kalan bir yedek sigortadır.
                </p>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▲ LONG:</strong> MA50&gt;MA200, {isEn ? 'price near MA50' : 'fiyat MA50\'ye yakın'}, RSI≥{isEn ? 'Threshold' : 'Eşik'}
            </div>
            <div style={{ background: 'rgba(255,59,59,0.08)', border: '1px solid rgba(255,59,59,0.3)', borderRadius: 6, padding: '8px 12px', flex: 1, fontSize: 12 }}>
              <strong>▼ SHORT:</strong> MA50&lt;MA200, {isEn ? 'price near MA50' : 'fiyat MA50\'ye yakın'}, RSI≤(100−{isEn ? 'Threshold' : 'Eşik'}) ({isEn ? 'futures only' : 'yalnızca vadeli'})
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '6px 10px' }}>
            {isEn
              ? 'If the trend reverses (MA50 crosses to the other side), the position closes immediately — a structural safety exit. No fixed TP, no trailing yet; the fixed SL stays active as a backstop. On spot, when MA50 turns down it stays flat, awaiting the next LONG signal.'
              : 'Trend tersine dönerse (MA50 karşı tarafa geçerse) pozisyon anında kapanır — yapısal güvenlik çıkışı. Sabit TP yok, henüz trailing de yok; sabit SL yedek sigorta olarak aktif kalır. Spot\'ta MA50 aşağı dönünce flat kalınır, sonraki LONG sinyali beklenir.'}
          </div>
        </div>
      )}

    </div>
  );
}

export default function Settings({ token, userType, onClose, onSave }) {
  const { t } = useTranslation();
  const { refresh: refreshUserSettings } = useUserSettings();
  const FIELD_META = getFieldMeta(t);
  const TABS = getAllTabs(t).filter(tb => {
    if (tb.systemOnly) return userType === 'system';
    if (tb.notAdmin)   return userType !== 'admin';
    return true;
  });
  const [tab,          setTab]          = useState('okx');
  const [form,         setForm]         = useState({});
  const [show,         setShow]         = useState({});
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState(null);
  const [showRiskHelp, setShowRiskHelp] = useState(false);
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  useTrackPageView(`Ayarlar - ${TABS.find(tb => tb.id === tab)?.label || tab}`);

  useEffect(() => {
    fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setForm({ ...FIELD_DEFAULTS, ...data }))
      .catch(() => setMsg({ text: t('settings.loadFailed'), type: 'error' }));
  }, [token]); // eslint-disable-line

  const handleChange = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleShow   = (key) => setShow(s => ({ ...s, [key]: !s[key] }));

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const normalized = { ...form };
      for (const [key, meta] of Object.entries(FIELD_META)) {
        if (meta.type === 'number' && normalized[key] != null) {
          normalized[key] = String(normalized[key]).replace(',', '.');
        }
      }
      const res = await fetch('/api/settings', {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify(normalized),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(
          d.error === 'exchange_switch_blocked' ? t('settings.exchangeSwitchBlocked')
          : d.error === 'okx_withdraw_permission' ? t('settings.okxWithdrawPermission')
          : t('settings.saveFailed')
        );
      }
      setMsg({ text: t('settings.saved'), type: 'ok' });
      setTimeout(() => setMsg(null), 3000);
      refreshUserSettings();
      onSave?.();
    } catch (err) {
      setMsg({ text: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const currentStrategy = form.analysis_strategy;
  const currentBaseUrl  = form.okx_base_url || FIELD_DEFAULTS.okx_base_url;
  // sl_pct alanı tüm stratejilerde AYNI (evrensel), ama rsi_rsima/rsima50'de 0/boş "ATR kullan"
  // değil "SL KAPALI" anlamına gelir (bu 2 stratejide ATR fallback'ı yok) — sadece hint metni
  // override edilir, alanın kendisi/davranışı diğer stratejilerde DEĞİŞMEZ.
  const isPctOnlyStrategy = currentStrategy === 'rsi_rsima' || currentStrategy === 'rsima50';
  const fields      = Object.entries(FIELD_META)
    .filter(([, m]) => m.tab === tab && (!m.strategies || m.strategies.includes(currentStrategy)) && (!m.platform || m.platform === currentBaseUrl))
    .map(([key, m]) => (key === 'sl_pct' && isPctOnlyStrategy) ? [key, { ...m, hint: t('settings.fieldSlPctPctOnly.hint') }] : [key, m]);
  const isUsersTab  = tab === 'users';
  const isSignalTab  = tab === 'signal';
  const isWalletTab  = tab === 'wallet';
  const isSmsTab     = tab === 'sms';
  const isSecurityTab = tab === 'security';
  const isBackupsTab  = tab === 'backups';

  return (
    <>
    {showRiskHelp && <RiskHelp onClose={() => setShowRiskHelp(false)} />}
    <div className="settings-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="settings-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>
        <div className="settings-header" onMouseDown={handleHeaderMouseDown}>
          <span className="settings-title">{t('settings.title')}</span>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>

        <div className="settings-tabs">
          {TABS.map(tb => (
            <button key={tb.id} className={`settings-tab ${tab === tb.id ? 'active' : ''}`} onClick={() => setTab(tb.id)}>
              {tb.label}
            </button>
          ))}
        </div>

        <div className="settings-body">
          {tab === 'okx' && (
            <p className="settings-note">
              {t('settings.apiNote')}
            </p>
          )}
          {tab === 'risk' && (
            <div className="settings-note settings-note-row">
              <span>{t('settings.riskNote')}</span>
              <button className="settings-help-btn" onClick={() => setShowRiskHelp(true)}>{t('settings.riskHelp')}</button>
            </div>
          )}

          {isUsersTab ? (
            <UserManager token={token} globalForm={form} onGlobalChange={handleChange} />
          ) : isSignalTab ? (
            <SignalInfo form={form} onFieldChange={handleChange} />
          ) : isWalletTab ? (
            <WalletTransfer token={token} />
          ) : isSmsTab ? (
            <SmsSettings token={token} />
          ) : isSecurityTab ? (
            <SecuritySettings token={token} />
          ) : isBackupsTab ? (
            <BackupSettings />
          ) : (
            fields.map(([key, meta]) => (
              <div className="settings-field" key={key}>
                <label className="settings-label">{meta.label}</label>
                {meta.hint && <span className="settings-hint">{meta.hint}</span>}
                <div className="settings-input-row">
                  {meta.type === 'select' ? (
                    <select
                      className="settings-input"
                      value={form[key] ?? ''}
                      onChange={e => handleChange(key, e.target.value)}
                    >
                      {meta.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input
                      className="settings-input"
                      type={meta.secret && !show[key] ? 'password' : (meta.type || 'text')}
                      value={form[key] ?? ''}
                      placeholder={meta.secret ? t('settings.secretPlaceholder') : ''}
                      onChange={e => handleChange(key, e.target.value)}
                    />
                  )}
                  {meta.unit && <span className="settings-unit">{meta.unit}</span>}
                  {meta.secret && form[key] && !String(form[key]).startsWith('••') && (
                    <button className="settings-eye" onClick={() => toggleShow(key)} type="button">
                      {show[key] ? '🙈' : '👁'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {!isUsersTab && !isWalletTab && !isSmsTab && !isSecurityTab && !isBackupsTab && (
          <div className="settings-footer">
            {msg && <span className={`settings-msg settings-msg-${msg.type}`}>{msg.text}</span>}
            <button className="btn btn-start" style={{ minWidth: 120 }} onClick={handleSave} disabled={saving}>
              {saving ? t('settings.saving') : t('settings.save')}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
